<template>
	<div class="flex h-full flex-1 flex-col items-center justify-between gap-100 overflow-y-auto md:gap-200">
		<InlineSpinner v-if="global.loggedIn && global.hubsLoading" />
		<draggable
			class="list-group flex w-full flex-1 flex-col gap-100 overflow-x-hidden p-200"
			:delay="300"
			:delay-on-touch-only="true"
			ghost-class="hub-drag-ghost"
			group="hubs"
			:item-key="'hubId'"
			:list="global.pinnedHubs"
			:touch-start-threshold="5"
			@contextmenu="preventNativeContextMenu"
			@end="onDragEnd"
			@start="onDragStart"
		>
			<template #item="{ element }">
				<div
					v-if="hubs.hub(element.hubId)"
					class="flex h-auto justify-center gap-200"
					@contextmenu.prevent="openHubContextMenu($event, element)"
				>
					<router-link
						v-slot="{ isActive, href, navigate }"
						custom
						:to="{ name: 'hub', params: { name: element.hubName } }"
					>
						<a
							v-if="global.loggedIn || element.hubId === hubs.currentHubId"
							class="w-full"
							:href="href"
							:aria-current="isActive ? 'true' : undefined"
							:aria-disabled="isActive ? 'true' : undefined"
							@click="
								navigate($event);
								sendToHub();
							"
						>
							<HubMenuHubIcon
								:active="isActive"
								class="text-on-surface"
								:hub-id="element.hubId"
								:pinned="true"
							/>
						</a>
					</router-link>
				</div>
			</template>
		</draggable>
		<div
			class="relative h-600 max-h-0 w-full overflow-hidden transition-all duration-300 ease-in-out"
			:class="{ 'max-h-600': isDragging }"
		>
			<div class="absolute grid h-full w-full items-center justify-center">
				<Icon
					:class="[hoverOverHubremoval ? 'text-on-accent-red' : 'text-accent-red']"
					class="p-050 rounded-md"
					type="trash"
				/>
			</div>
			<draggable
				class="list-group h-full opacity-0"
				group="hubs"
				:item-key="'unpin'"
				:list="unpinnedHubs"
				tag="ul"
				@change="confirmationHubRemoval"
				@dragleave="hoverOverHubremoval = false"
				@dragover="hoverOverHubremoval = true"
			>
				<template #item="{ element: trash }">
					<li>{{ trash }}</li>
				</template>
			</draggable>
		</div>
	</div>
</template>

<script lang="ts" setup>
	// Packages
	import { ref } from 'vue';
	import { useI18n } from 'vue-i18n';
	import draggable from 'vuedraggable';

	// Components
	import HubMenuHubIcon from '@global-client/components/ui/HubMenuHubIcon.vue';

	import Icon from '@hub-client/components/elements/Icon.vue';
	// Hub imports
	import InlineSpinner from '@hub-client/components/ui/InlineSpinner.vue';

	// Models
	import { type MenuItem } from '@hub-client/models/components/contextMenu.models';

	// Stores
	import { type PinnedHub, type PinnedHubs, useGlobal } from '@global-client/stores/global';
	import { useHubs } from '@global-client/stores/hubs';
	import { useToggleMenu } from '@global-client/stores/toggleGlobalMenu';

	import { useContextMenuStore } from '@hub-client/stores/contextMenu.store';
	import { useDialog } from '@hub-client/stores/dialog';
	import { useMessageBox } from '@hub-client/stores/messagebox';

	const global = useGlobal();
	const hubs = useHubs();
	const toggleMenu = useToggleMenu();
	const dialog = useDialog();
	const contextMenu = useContextMenuStore();
	const { t } = useI18n();
	const isDragging = ref(false);
	const hoverOverHubremoval = ref(false);

	let backupPinnedHubs = [] as PinnedHubs;
	let unpinnedHubs = [] as PinnedHubs;

	function preventNativeContextMenu(event: Event) {
		// Only prevent the browser's native context menu on touch devices to avoid
		// interfering with the drag delay. Custom context menus can still be triggered.
		if (window.matchMedia('(pointer: coarse)').matches) {
			event.preventDefault();
		}
	}

	function onDragStart() {
		backupPinnedHubs = global.pinnedHubs.slice();
		isDragging.value = true;
	}

	function onDragEnd() {
		isDragging.value = false;
		hoverOverHubremoval.value = false;
	}

	function sendToHub() {
		toggleMenu.hideMenuAndSendToHub();
	}

	// Right-clicking a hub offers "Unpin" as a discoverable alternative to the drag-to-trash gesture.
	function openHubContextMenu(event: MouseEvent, hub: PinnedHub) {
		const items: MenuItem[] = [{ label: t('dialog.hub_unpin_title'), icon: 'trash', isDelicate: true, onClick: () => unpinHub(hub) }];
		contextMenu.open(items, event.clientX, event.clientY);
	}

	async function unpinHub(hub: PinnedHub) {
		const confirmed = Boolean(await dialog.yesno(t('dialog.hub_unpin_title'), t('dialog.hub_unpin_context'), 'global'));
		if (!confirmed) return;
		const index = global.pinnedHubs.findIndex((pinnedHub) => pinnedHub.hubId === hub.hubId);
		if (index < 0) return;
		global.removePinnedHub(index);
		const messagebox = useMessageBox();
		messagebox.resetMiniclient(hub.hubId);
	}

	async function confirmationHubRemoval() {
		let removeHub = Boolean(await dialog.yesno(t('dialog.hub_unpin_title'), t('dialog.hub_unpin_context'), 'global'));

		if (removeHub) {
			backupPinnedHubs.splice(0, backupPinnedHubs.length);
			const messagebox = useMessageBox();
			messagebox.resetMiniclient(unpinnedHubs[0].hubId);
		} else {
			global.pinnedHubs = backupPinnedHubs.slice();
			backupPinnedHubs.splice(0, backupPinnedHubs.length);
		}
		unpinnedHubs = [];
	}
</script>
